import { ponsRewardNftABI } from "../abis/pons-reward-nft";
import { decodeEventLog, decodeFunctionData, encodeAbiParameters, encodeFunctionData, getAddress, keccak256,
  stringToHex, type Address, type Hex } from "viem";
import type { z } from "zod";
import type { mintAuthorizationSchema } from "../api/schemas";

// Shared full ABI supplied for the collection; deployment permission is checked separately.
export const ponsRewardNftAbi = ponsRewardNftABI;

export function encodeRedeemCall(input: {
  tokenId: bigint;
  minPayoutWei: bigint;
  deadline: bigint;
}): Hex {
  if (input.tokenId < 0n || input.minPayoutWei < 0n || input.deadline < 0n) throw new RangeError("invalid redeem input");
  return encodeFunctionData({
    abi: ponsRewardNftAbi,
    functionName: "redeem",
    args: [input.tokenId, input.minPayoutWei, input.deadline],
  });
}

/** New route only. Encoding is not deployment support or permission to send. */
export function encodeRedeemWithFeesCall(input: {
  tokenId: bigint; minPayoutWei: bigint; deadline: bigint;
}): Hex {
  for (const value of [input.tokenId, input.minPayoutWei, input.deadline]) {
    if (typeof value !== "bigint" || value < 0n || value >= (1n << 256n)) throw new RangeError("Invalid redeemWithFees uint256");
  }
  if (input.minPayoutWei === 0n || input.deadline === 0n) throw new RangeError("A positive minimum and deadline are required");
  return encodeFunctionData({ abi: ponsRewardNftAbi, functionName: "redeemWithFees",
    args: [input.tokenId, input.minPayoutWei, input.deadline, true, []] });
}

export function findPendingMint(receipt: { logs: ReadonlyArray<{ address: Address; data: Hex; topics: readonly Hex[] }> },
  collectionAddress: Address): { tokenId: bigint; wallet: Address; sourceToken: Address;
    proofKey: Hex; authorizationId: Hex; selectedTierUsd: bigint; mintFeeWei: bigint; rewardPolicyHash: Hex; rewardPolicyVersion: bigint } | null {
  for (const log of receipt.logs) {
    if (log.address.toLowerCase() !== collectionAddress.toLowerCase()) continue;
    try {
      const decoded = decodeEventLog({ abi: ponsRewardNftAbi, data: log.data,
        topics: [...log.topics] as [Hex, ...Hex[]] });
      if (decoded.eventName !== "PendingNftMinted") continue;
      const args = decoded.args;
      return { tokenId: args.tokenId, wallet: getAddress(args.wallet), sourceToken: getAddress(args.sourceToken),
        proofKey: args.proofKey, authorizationId: args.authorizationId,
        selectedTierUsd: args.selectedTierUsd, mintFeeWei: args.mintFeeWei,
        rewardPolicyHash: args.rewardPolicyHash, rewardPolicyVersion: args.rewardPolicyVersion };
    } catch {
      // Other collection events are expected in the same receipt.
    }
  }
  return null;
}

export function findRewardRedeemed(receipt: {
  logs: ReadonlyArray<{ address: Address; data: Hex; topics: readonly Hex[] }>;
}, collectionAddress: Address): {
  tokenId: bigint;
  owner: Address;
  sourceToken: Address;
  payoutWei: bigint;
} | null {
  for (const log of receipt.logs) {
    if (log.address.toLowerCase() !== collectionAddress.toLowerCase()) continue;
    try {
      const decoded = decodeEventLog({ abi: ponsRewardNftAbi, data: log.data,
        topics: [...log.topics] as [Hex, ...Hex[]] });
      if (decoded.eventName !== "RewardRedeemed") continue;
      const args = decoded.args as { tokenId: bigint; owner: Address; sourceToken: Address; payoutWei: bigint };
      return { tokenId: args.tokenId, owner: getAddress(args.owner), sourceToken: getAddress(args.sourceToken),
        payoutWei: args.payoutWei };
    } catch {
      // Other collection events are expected in the same receipt.
    }
  }
  return null;
}

export type MintAuthorization = z.infer<typeof mintAuthorizationSchema>;

export function hasNftBurn(receipt: { logs: ReadonlyArray<{ address: Address; data: Hex; topics: readonly Hex[] }> },
  collection: string, owner: string, tokenId: string): boolean {
  return receipt.logs.some((log) => {
    if (log.address.toLowerCase() !== collection.toLowerCase()) return false;
    try {
      const event = decodeEventLog({ abi: ponsRewardNftAbi, data: log.data, topics: [...log.topics] as [Hex, ...Hex[]] });
      return event.eventName === "Transfer" && event.args.tokenId.toString() === tokenId &&
        event.args.from.toLowerCase() === owner.toLowerCase() && event.args.to === "0x0000000000000000000000000000000000000000";
    } catch { return false; }
  });
}

export function mintProofKey(input: { instanceId: Hex; chainId: bigint; challengeId: Hex; challengeCommitment: Hex }): Hex {
  return keccak256(encodeAbiParameters([
    { type: "bytes32" }, { type: "bytes32" }, { type: "uint256" }, { type: "bytes32" }, { type: "bytes32" },
  ], [keccak256(stringToHex("PONS_POW_NFT_PROOF_KEY_V1")), input.instanceId, input.chainId,
    input.challengeId, input.challengeCommitment]));
}

export function validatePreparedMintCalldata(data: Hex, envelope: MintAuthorization, expected: {
  wallet: Address;
  sourceToken: Address;
  selectedTierUsd: bigint;
  quotedMintFeeWei: bigint;
  deadline: bigint;
  chainId: number;
  collection: Address;
  signer: Address | null;
  authorizationId: Hex;
  proofKey: Hex;
  instanceId: Hex;
  challengeCommitment: Hex;
}): void {
  const decoded = decodeFunctionData({ abi: ponsRewardNftAbi, data });
  if (decoded.functionName !== "mint") throw new Error("Prepared call is not a mint transaction");
  const [authorization] = decoded.args;
  const message = envelope.message;
  // Re-encode for equality only. The original backend bytes are the only bytes sent.
  const canonical = encodeFunctionData({ abi: ponsRewardNftAbi, functionName: "mint", args: [{
    ...message, selectedTierUsd: BigInt(message.selectedTierUsd), rewardPolicyVersion: BigInt(message.rewardPolicyVersion),
    coverageRevision: BigInt(message.coverageRevision), asOfBlock: BigInt(message.asOfBlock),
    priceTimestamp: BigInt(message.priceTimestamp), quotedMintFeeWei: BigInt(message.quotedMintFeeWei),
    issuedAt: BigInt(message.issuedAt), deadline: BigInt(message.deadline),
  }, envelope.signature] });
  if (canonical.toLowerCase() !== data.toLowerCase()) {
    throw new Error("Prepared mint calldata differs from the backend authorization or signature");
  }
  if (envelope.chainId !== expected.chainId ||
    !sameHex(envelope.verifyingContract, expected.collection) ||
    expected.signer !== null && !sameHex(envelope.signerAddress, expected.signer) ||
    !sameHex(authorization.authorizationId, expected.authorizationId) ||
    !sameHex(authorization.instanceId, expected.instanceId) ||
    !sameHex(authorization.proofKey, expected.proofKey) ||
    !sameHex(authorization.challengeCommitment, expected.challengeCommitment)) {
    throw new Error("Prepared mint authorization does not match the active proof or deployment");
  }
  if (getAddress(authorization.wallet).toLowerCase() !== getAddress(expected.wallet).toLowerCase() ||
    getAddress(authorization.sourceToken).toLowerCase() !== getAddress(expected.sourceToken).toLowerCase() ||
    authorization.selectedTierUsd !== expected.selectedTierUsd ||
    authorization.quotedMintFeeWei !== expected.quotedMintFeeWei ||
    authorization.deadline !== expected.deadline) {
    throw new Error("Prepared mint calldata does not match the reviewed quote");
  }
}

function sameHex(a: string, b: string): boolean { return a.toLowerCase() === b.toLowerCase(); }
